import { Component, ElementRef, ViewChild } from '@angular/core';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { IonicSafeString, ModalController, ToastButton } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import { AddBankAccountModalComponent } from '@payments/components/add-bank-account-modal/add-bank-account-modal.component';
import { OPEN_AUTOPAY_MODAL } from '@payments/constants/constants';
import {
  BankAccount,
  PaymentPreferencesPageViewModel,
} from '@payments/models/payment-preferences-page-view-model';
import {
  AllFilterIds,
  ModalType,
  PaymentsModalService,
} from '@payments/services/payments-modal.service';
import { PaymentsService } from '@payments/services/payments.service';
import { ModalWithNavComponent } from '@shared/components/modal-with-nav/modal-with-nav.component';
import { LoadingSpinnerComponent } from '@shared/components/popovers/loading-spinner/loading-spinner.component';
import SingleListModalComponent from '@shared/components/single-list-modal/single-list-modal.component';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { ModalService } from '@shared/services/modal/modal.service';
import { ToastService } from '@shared/services/toast/toast.service';
import { BankStoreService, SortFilter } from '@usf/ngrx-payment';
import { BankAccountWarning } from '@usf/ngrx-payment/lib/models/bank-account-warning.model';
import { NewADBankAccount } from '@usf/ngrx-payment/lib/models/new-ad-bank-account.model';
import { BankDetailsState } from '@usf/ngrx-payment/lib/models/state/bank-details.state';
import { CustomerResponse, EligibleCustomer } from '@usf/payment-types';
import {
  Observable,
  Subject,
  Subscription,
  distinctUntilKeyChanged,
  filter,
  skip,
  take,
  takeUntil,
} from 'rxjs';
import { PaymentPreferencesService } from './services/payment-preferences.service';
import { PaymentPreferencesAnalyticsMessages } from './tracking/analytics/payment-preferences-analytics.constants';
import { PaymentPreferencesAnalyticsService } from './tracking/analytics/payment-preferences-analytics.service';

@Component({
  selector: 'app-payment-preferences',
  templateUrl: './payment-preferences.page.html',
  styleUrls: ['./payment-preferences.page.scss'],
})
export class PaymentPreferencesPage {
  @ViewChild('mainDivScroll', { static: false }) mainScroll: ElementRef;
  vm$: Observable<PaymentPreferencesPageViewModel>;
  autopayEnrollResponse$: Subscription;
  presentedToast: Promise<HTMLIonToastElement>;
  userName: string;
  isBankRoutingValidationOn: boolean;

  destroyed$ = new Subject<void>();

  constructor(
    readonly platformService: PlatformService,
    private paymentPreferencesService: PaymentPreferencesService,
    readonly panAppState: PanAppState,
    private paymentPreferencesAnalyticsService: PaymentPreferencesAnalyticsService,
    private paymentsService: PaymentsService,
    private modalService: ModalService,
    private modalController: ModalController,
    private translateService: TranslateService,
    private bankStoreService: BankStoreService,
    private toastService: ToastService,
    private paymentsModalService: PaymentsModalService,
  ) {}

  ionViewWillEnter() {
    this.paymentsService.getEligibleADCustomers();

    this.vm$ = this.paymentPreferencesService
      .getPaymentPreferencesPageViewModel$()
      .pipe(takeUntil(this.destroyed$));
    this.panAppState.setPageTitle(PageTitle.paymentPreferences);
    this.trackPageLoad();

    this.vm$
      .pipe(distinctUntilKeyChanged('filters'), takeUntil(this.destroyed$))
      .subscribe(_ => {
        this.paymentPreferencesService.getBankDetails();
      });

    this.vm$
      .pipe(
        distinctUntilKeyChanged('isBankRoutingValidationOn'),
        takeUntil(this.destroyed$),
      )
      .subscribe(vm => {
        this.isBankRoutingValidationOn = vm.isBankRoutingValidationOn;
      });

    this.panAppState.user$.pipe(takeUntil(this.destroyed$)).subscribe(user => {
      const nameParts = [user.firstName, user.lastName].filter(name => !!name);
      this.userName = nameParts.join(' ');
    });
  }

  ionViewDidEnter() {
    const navigationState = history.state;
    if (!!navigationState && (navigationState[OPEN_AUTOPAY_MODAL] ?? false)) {
      this.getBankAccountForAutopay();
    }
  }

  trackPageLoad() {
    this.vm$.pipe(skip(1), take(1)).subscribe(vm => {
      this.paymentPreferencesAnalyticsService.trackPageLoad(
        vm.autopayBankAccounts.length,
        vm.manualDeductBankAccounts.length,
        vm.customersCount,
      );
    });
  }

  handleToastsForWarnings(warning: BankAccountWarning) {
    warning.warningList.forEach(warning => {
      this.paymentsService.showToastMessage(
        this.translateService.instant(
          'i18n.payments.paymentPreferences.autopayWarningMsg',
          {
            customerNumber: warning.messageText,
          },
        ),
        true,
      );
    });
  }

  displayAutopaySetupResultsToast(
    autopaySetupResults: CustomerResponse[],
    bankAccount: NewADBankAccount,
  ) {
    const successCount = autopaySetupResults.filter(
      result => !result.errorFlag,
    ).length;
    const totalCount = autopaySetupResults.length;
    const { message, icon } = this.getAutopaySetupToastMessageAndIcon(
      successCount,
      totalCount,
    );
    const buttons = this.getAutopaySetupToastButtons(
      successCount,
      totalCount,
      autopaySetupResults,
      bankAccount,
    );

    if (this.presentedToast) {
      this.presentedToast.then(toast => toast.dismiss());
    }

    this.presentedToast = this.toastService.presentToast({
      id: 'autopay-setup-results',
      verticalLayout:
        this.platformService.getPlatformType() == PlatformEnum.mobile,
      type: MessageTypeEnum.informational,
      display: new IonicSafeString(
        this.translateService.instant(message, {
          successCount,
          totalCount,
        }),
      ),
      buttons,
      stack: null,
      watermark: new Date().toUTCString(),
      read: false,
      sticky: true,
      icon,
    });
  }

  handleAutopaySetup = (isAutopayEnabled: boolean): void => {
    if (isAutopayEnabled) {
      this.getBankAccountForAutopay();
      return;
    }
    this.paymentsService.goToLegacyPlatform(this.platformService.isTouch.value);
  };

  async openAutopayFailedCustomersModal(customers: string[]) {
    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      SingleListModalComponent,
      {
        title: 'i18n.payments.paymentPreferences.autopaySetup.failedCustomers',
        listItems: customers,
      },
      'modal-desktop-size-lg',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    await modal.present();
  }

  async openAutopayEdit(canSaveModal: boolean, bankDetail?: BankAccount) {
    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      ModalWithNavComponent,
      {
        rootPage: AddBankAccountModalComponent,
        rootPageParams: {
          canSave: canSaveModal,
          canSelectCustomers: !bankDetail,
          currentBankAccount: bankDetail,
          customerNumber: bankDetail?.customerNumber,
          isEditingAutopay: !!bankDetail,
          isBankRoutingValidationOn: this.isBankRoutingValidationOn,
        },
      },
      'modal-desktop-size-lg',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    await modal.present();
    const {
      data: { isSaved, bankAccount },
    } = await modal.onDidDismiss();
    if (bankAccount) {
      if (isSaved) {
        this.bankStoreService.postBankAccount(bankAccount);
        this.getBankDetails(
          this.translateService.instant(
            'i18n.payments.paymentPreferences.failActionToBankAccountMsg',
            {
              action: this.translateService
                .instant('i18n.common.edit')
                .toLowerCase(),
            },
          ),
        );
      }
    }
  }

  openBankAccountModal = async (
    canSaveModal: boolean,
    bankDetail?: BankAccount,
  ) => {
    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      ModalWithNavComponent,
      {
        rootPage: AddBankAccountModalComponent,
        rootPageParams: {
          canSelectCustomers: !bankDetail,
          isEditingBankAccount: !!bankDetail,
          currentBankAccount: bankDetail,
          customerNumber: bankDetail?.customerNumber,
          canSave: canSaveModal,
          isBankRoutingValidationOn: this.isBankRoutingValidationOn,
        },
      },
      'modal-desktop-size-lg',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    await modal.present();
    const {
      data: { isSaved, bankAccount },
    } = await modal.onDidDismiss();
    if (bankAccount) {
      if (isSaved) {
        !bankDetail &&
          this.paymentPreferencesAnalyticsService.trackAddNewAccounts(
            bankAccount.customerNumbers.length,
            PaymentPreferencesAnalyticsMessages.manualDeductType,
            PaymentPreferencesAnalyticsMessages.newBankAccount,
          );
        this.bankStoreService.postBankAccount(bankAccount);
        this.getBankDetails(
          this.translateService.instant(
            'i18n.payments.paymentPreferences.failActionToBankAccountMsg',
            {
              action: this.translateService
                .instant(bankDetail ? 'i18n.common.edit' : 'i18n.common.create')
                .toLowerCase(),
            },
          ),
        );
      }
    }
  };

  async getBankAccountForAutopay() {
    this.paymentPreferencesService.getUniqueBankAccounts();
    const loadingSpinnerModal = await this.loadingModal(
      'i18n.payments.paymentPreferences.preloadingAutopay',
    );
    (
      this.bankStoreService.getBanksDetails$() as unknown as Observable<BankDetailsState>
    )
      .pipe(
        filter(bankState => !bankState.loading),
        take(1),
      )
      .subscribe(state => {
        loadingSpinnerModal?.dismiss();
        this.openSetUpAutopayModal(
          state.uniqueBankAccounts as BankAccount[],
          state.eligibleADCustomers,
        );
      });
  }

  async openSetUpAutopayModal(
    bankAccounts: BankAccount[],
    eligibleADCustomers: EligibleCustomer[],
  ) {
    this.paymentPreferencesAnalyticsService.trackAutopaySetupStarted();
    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      ModalWithNavComponent,
      {
        rootPage: AddBankAccountModalComponent,
        rootPageParams: {
          showSegments: true,
          canSelectCustomers: true,
          isSettingUpAutopay: true,
          bankAccounts,
          eligibleADCustomers,
          isBankRoutingValidationOn: this.isBankRoutingValidationOn,
        },
      },
      'modal-desktop-size-lg',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    await modal.present();
    const {
      data: { isSaved, bankAccount },
    } = await modal.onDidDismiss();
    if (bankAccount) {
      if (isSaved) {
        const newOrExistingAccount = !!bankAccounts.find(
          account => account.bankID === bankAccount.bankID,
        )
          ? PaymentPreferencesAnalyticsMessages.existingBankAccount
          : PaymentPreferencesAnalyticsMessages.newBankAccount;

        this.paymentPreferencesAnalyticsService.trackAddNewAccounts(
          bankAccount.customerNumbers.length,
          PaymentPreferencesAnalyticsMessages.autoDeductType,
          newOrExistingAccount,
        );

        this.bankStoreService.postADBankAccount(bankAccount);

        this.getBankDetails();
        this.observeAutopaySetupResults(bankAccount);
      }
    }
  }

  observeAutopaySetupResults(bankAccount: NewADBankAccount) {
    this.autopayEnrollResponse$ = this.vm$
      .pipe(
        filter(vm =>
          bankAccount.customerNumbers.every(
            customerNumber =>
              !!vm.enrolledADCustomersResponse?.find(
                response => response.customerNumber == customerNumber,
              ),
          ),
        ),
        take(1),
      )
      .subscribe(vm => {
        this.displayAutopaySetupResultsToast(
          vm.enrolledADCustomersResponse,
          bankAccount,
        );
      });
  }

  removeBankAccount({ bankID }: { bankID: string }) {
    this.bankStoreService.removeBankAccount(bankID);
    this.getBankDetails();
  }

  async loadingModal(
    loadingMessage = 'i18n.payments.reviewAndSubmit.saving',
  ): Promise<HTMLIonModalElement> {
    const loadingSpinnerModal = await this.modalController.create({
      component: LoadingSpinnerComponent,
      backdropDismiss: false,
      cssClass: 'modal-loading-spinner',
      componentProps: {
        loadingSpinnerModalMessage:
          this.translateService.instant(loadingMessage),
      },
    });

    await loadingSpinnerModal?.present();

    return loadingSpinnerModal;
  }

  async getBankDetails(errorMessage?: string) {
    const loadingSpinnerModal = await this.loadingModal();

    (
      this.bankStoreService.getBanksDetails$() as unknown as Observable<BankDetailsState>
    )
      .pipe(
        filter(bankState => !bankState.loading),
        take(1),
      )
      .subscribe(state => {
        loadingSpinnerModal?.dismiss();
        if (state.error) {
          this.paymentsService.showToastMessage(
            errorMessage || state.error.message,
            true,
          );
          this.bankStoreService.clearError();
        } else {
          if (state.warning) {
            this.handleToastsForWarnings(state.warning);
          }
          this.paymentPreferencesService.getBankDetails();
        }
      });
  }

  openFilterModal(selectedFilter?: AllFilterIds) {
    this.paymentsModalService.openModal(
      this.paymentPreferencesService,
      selectedFilter,
      false,
      ModalType.paymentPreferences,
    );
  }

  setSortField(sortFilter: SortFilter) {
    this.paymentPreferencesService.setSort([sortFilter]);
  }

  resetFilters() {
    this.paymentPreferencesService.clearFiltersAndResetSort();
  }

  getAutopaySetupToastMessageAndIcon(
    successCount: number,
    totalCount: number,
  ): { message: string; icon: string } {
    const isMobile =
      this.platformService.getPlatformType() != PlatformEnum.desktop;
    let icon = 'alert-circle-outline';
    let message = 'i18n.payments.paymentPreferences.autopaySetup.';

    if (isMobile) {
      message += 'mobile.';
    }

    if (successCount == totalCount) {
      message += 'success';
      icon = 'checkmark-circle-outline';
    } else if (successCount > 0) {
      message += 'partialSuccess';
    } else {
      message += 'failure';
    }

    if (isMobile) {
      icon = null;
    }

    return { message, icon };
  }

  getAutopaySetupToastButtons(
    successCount: number,
    totalCount: number,
    autopaySetupResults: CustomerResponse[],
    bankAccount: NewADBankAccount,
  ): ToastButton[] {
    const closeModalOnClick = false;
    let buttons: ToastButton[] = [];
    if (successCount) {
      buttons.push({
        text: this.translateService.instant('i18n.common.downloadPdf'),
        side: 'end',
        handler: () => {
          this.paymentsService.downloadAutopayAchForm(
            bankAccount,
            this.userName,
          );
          return closeModalOnClick;
        },
      });
    }
    if (successCount != totalCount) {
      buttons.push({
        text: this.translateService.instant('i18n.common.viewErrors'),
        side: 'end',
        handler: () => {
          this.vm$.pipe(take(1)).subscribe(vm => {
            let failedCustomers = vm.eligibleADCustomers.filter(customer =>
              autopaySetupResults.find(
                setupResult =>
                  setupResult.customerNumber === customer.customerNumber &&
                  setupResult.errorFlag,
              ),
            );
            let formattedCustomers = failedCustomers.map(
              failedCustomer =>
                `${failedCustomer.customerName} (${failedCustomer.customerNumber})`,
            );
            this.openAutopayFailedCustomersModal(formattedCustomers);
          });

          return closeModalOnClick;
        },
      });
    }
    return buttons;
  }

  ionViewWillLeave(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.autopayEnrollResponse$?.unsubscribe();
    this.presentedToast?.then(toast => toast.dismiss());
  }
}
